"use client";

import { Mail, MessageCircle, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurSimple3StepProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    icon: Mail,
    title: "Request your inspection",
    description: "Contact us to schedule a professional roof assessment. Our team will evaluate your property's condition and provide a detailed report within 48 hours.",
    time: "Response within 48 hours",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_inspection.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_inspection.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_inspection.png-wvc-sizes"
  }, {
    number: "02",
    icon: MessageCircle,
    title: "Expert consultation",
    description: "We discuss your specific needs, whether it's a repair or a full replacement. We provide transparent pricing and guide you through insurance claim assistance.",
    time: "30 — 45 minute session",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_consultation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_consultation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_consultation.png-wvc-sizes"
  }, {
    number: "03",
    icon: Sparkles,
    title: "Quality installation",
    description: "Our certified team executes the project with precision and care. We ensure your home is protected with high-quality materials and industry-leading craftsmanship.",
    time: "2 — 4 day project",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_installation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_installation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1716391/img/roof_installation.png-wvc-sizes"
  }];
  return <section data-section-id="1382" id="our-simple-3-step-process" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-[1320px] mx-auto">
        {}
        <div ref={headerRef} className={`max-w-2xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          {}
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="h-px w-12 bg-border" />
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
              ·iii — our process
            </span>
            <span className="h-px w-12 bg-border" />
          </div>

          <h2 className="font-default font-semibold text-foreground leading-[1.05] tracking-tight text-4xl md:text-5xl lg:text-6xl">
            Three{" "}
            <span className="font-serif italic font-normal text-primary">
              simple
            </span>{" "}
            steps to a secure home
          </h2>

          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            Professional roofing solutions delivered with integrity and speed.
          </p>
        </div>

        {}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className="relative group">
                {}
                {i < steps.length - 1 && <div className="hidden md:flex absolute top-1/2 -right-3 lg:-right-4 -translate-y-1/2 z-10 items-center pointer-events-none">
                    <div className="flex items-center">
                      <span className="h-px w-3 lg:w-4 bg-border" />
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-ring/60">
                        <path d="M7 2 C 5 5, 5 9, 7 12 C 9 9, 9 5, 7 2 Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="h-px w-3 lg:w-4 bg-border" />
                    </div>
                  </div>}

                {}
                {i < steps.length - 1 && <div className="md:hidden flex justify-center mt-8 -mb-4">
                    <svg width="16" height="32" viewBox="0 0 16 32" fill="none" className="text-ring/60">
                      <line x1="8" y1="0" x2="8" y2="20" stroke="currentColor" strokeWidth="1" strokeDasharray="2 3" />
                      <path d="M8 22 C 6 25, 6 29, 8 32 C 10 29, 10 25, 8 22 Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>}

                <StepCard step={step} Icon={Icon} index={i} />
              </div>;
        })}
        </div>
      </div>
    </section>;
}
function StepCard({
  step,
  Icon,
  index
}: {
  step: {
    number: string;
    title: string;
    description: string;
    time: string;
    image: string;
    srcset: string;
    sizes: string;
  };
  Icon: React.ElementType;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} className={`transition-all ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDuration: "700ms",
    transitionDelay: `${index * 80}ms`,
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
  }}>
      <Card className="relative h-full bg-card border border-border p-7 transition-all duration-300 hover:border-ring overflow-hidden group">
        {}
        <div className="flex items-start justify-between mb-6">
          <div className="relative">
            <svg width="56" height="64" viewBox="0 0 56 64" fill="none" className="text-ring transition-colors duration-300 group-hover:text-primary">
              <path d="M28 6 C 14 22, 10 38, 16 50 C 21 60, 35 60, 40 50 C 46 38, 42 22, 28 6 Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="fill-transparent group-hover:fill-primary/20 transition-all duration-300" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center pt-2 font-mono text-sm font-medium text-primary">
              {step.number}
            </span>
          </div>

          {}
          <div className="relative w-14 h-14 overflow-hidden border border-ring/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out" style={{
          transitionDelay: "50ms"
        }}>
            <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="absolute top-1 right-1 text-ring">
              <path d="M4 1 L5 3 L7 4 L5 5 L4 7 L3 5 L1 4 L3 3 Z" fill="currentColor" />
            </svg>
          </div>
        </div>

        {}
        <div className="mb-4">
          <Icon className="w-5 h-5 text-ring" strokeWidth={1.5} />
        </div>

        {}
        <h3 className="font-default font-semibold text-xl md:text-2xl text-card-foreground leading-tight mb-3 tracking-tight">
          {step.title}
        </h3>

        {}
        <p className="text-[15px] leading-relaxed text-muted-foreground mb-6">
          {step.description}
        </p>

        {}
        <div className="flex items-center gap-2 mb-3">
          <span className="h-px flex-1 bg-border" />
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="text-ring/70">
            <path d="M2 5 C 3 3, 5 2, 7 2 M7 2 C 9 2, 11 3, 12 5 M7 2 L7 8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {}
        <p className="font-mono text-xs uppercase tracking-[0.04em] text-ring">
          {step.time}
        </p>
      </Card>
    </div>;
}